import React from 'react';
import PropTypes from 'prop-types';
import { useLeagueSettings } from '../context/LeagueSettingsContext';
import HiddenText from './HiddenText';
import styles from '../styles/LeagueLockItem.module.css';
import { formatGameDateTime } from '../utils/timeUtils';

const LockClosedIcon = () => (
  <svg className={styles.lockIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32px" height="32px">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
  </svg>
);

const LeagueLockItem = ({ lock, currentUserId, weekNumber, hasUserReachedMaxLocks, userLocksCount = 0, maxPicks = 1 }) => {
  const { canSeeLockers, canSeeLocks } = useLeagueSettings();

  // Determine what to show based on league settings
  const isCurrentUser = lock.userId === currentUserId;
  const showUsername = isCurrentUser || canSeeLockers(weekNumber, isCurrentUser, userLocksCount, maxPicks) || hasUserReachedMaxLocks;
  const showPickInfo = canSeeLocks() || hasUserReachedMaxLocks;
  const formatLine = (line) => {
    if (line === undefined || line === null) return 'N/A';
    return line > 0 ? `+${line}` : line.toString();
  };

  // Handle bye week display
  if (lock.pickType === 'bye') {
    return (
      <div className={`${styles.leagueLockItem} ${lock.userId === currentUserId ? styles.currentUser : ''}`}>
        <div className={styles.lockIconWrapper}>
          <LockClosedIcon />
        </div>
        <div className={styles.contentWrapper}>
          <div className={styles.userSection}>
            <span className={styles.username}>
              {showUsername ? (
                lock.username
              ) : (
                <HiddenText
                  type="locker"
                  weekNumber={weekNumber}
                  showTooltip={true}
                />
              )}
            </span>
            <span className={styles.timestamp}>
              {formatGameDateTime(lock.createdAt).headerDate},
              {formatGameDateTime(lock.createdAt).displayTime}
            </span>
          </div>
          <div className={styles.pickSection}>
            <span className={styles.byeWeekLabel}>Bye</span>
            <span className={styles.byeWeekNumber}>Week {lock.weekNumber}</span>
          </div>
        </div>
      </div>
    );
  }

  // Handle no pick display
  if (lock.pickType === 'no_pick') {
    return (
      <div className={`${styles.leagueLockItem} ${lock.userId === currentUserId ? styles.currentUser : ''}`}>
        <div className={styles.lockIconWrapper}>
          <LockClosedIcon />
        </div>
        <div className={styles.contentWrapper}>
          <div className={styles.userSection}>
            <span className={styles.username}>
              {showUsername ? (
                lock.username
              ) : (
                <HiddenText
                  type="locker"
                  weekNumber={weekNumber}
                  showTooltip={true}
                />
              )}
            </span>
            <span className={styles.timestamp}>
              {formatGameDateTime(lock.createdAt).headerDate},
              {formatGameDateTime(lock.createdAt).displayTime}
            </span>
          </div>
          <div className={styles.pickSection}>
            <span className={styles.pickDetails}>No Pick</span>
          </div>
          <div className={styles.gameInfo}>
            Week {lock.weekNumber}
          </div>
        </div>
      </div>
    );
  }

  // Regular pick display logic
  const getPickDisplay = () => {
    if (lock.pickType === 'over') return `O${Math.abs(lock.lockLine)}`;
    if (lock.pickType === 'under') return `U${Math.abs(lock.lockLine)}`;
    return `${lock.team} ${formatLine(lock.lockLine)}`;
  };

  const lockDateTime = formatGameDateTime(lock.createdAt);
  const gameDateTime = formatGameDateTime(lock.startDate);

  return (
    <div className={`${styles.leagueLockItem} ${isCurrentUser ? styles.currentUser : ''}`}>
      <div className={styles.lockIconWrapper}>
        <LockClosedIcon />
      </div>
              <div className={styles.contentWrapper}>
          <div className={styles.userSection}>
            <span className={styles.username}>
              {showUsername ? (
                lock.username
              ) : (
                <HiddenText
                  type="locker"
                  weekNumber={weekNumber}
                  showTooltip={true}
                />
              )}
            </span>
            <span className={styles.timestamp}>
              {lockDateTime.headerDate}, {lockDateTime.displayTime}
            </span>
          </div>
          <div className={styles.pickSection}>
            <span className={styles.pickDetails}>
              {showPickInfo ? (
                getPickDisplay()
              ) : (
                <HiddenText
                  type="lock"
                  weekNumber={weekNumber}
                  showTooltip={true}
                />
              )}
            </span>
          </div>
          <div className={styles.gameInfo}>
            {showPickInfo ? (
              <>
                {`${lock.awayTeam} @ ${lock.homeTeam}`}
                {gameDateTime.displayDate !== 'Invalid Date' && (
                  <span>, {gameDateTime.headerDate}, {gameDateTime.displayTime}</span>
                )}
              </>
            ) : (
              <HiddenText
                type="lock"
                weekNumber={weekNumber}
                showTooltip={true}
              />
            )}
          </div>
        </div>
    </div>
  );
};

LeagueLockItem.propTypes = {
  lock: PropTypes.shape({
    id: PropTypes.number.isRequired,
    username: PropTypes.string.isRequired,
    userId: PropTypes.number.isRequired,
    team: PropTypes.string,
    awayTeam: PropTypes.string,
    homeTeam: PropTypes.string,
    pickType: PropTypes.string.isRequired,
    lockLine: PropTypes.number,
    createdAt: PropTypes.string.isRequired,
    startDate: PropTypes.string,
    weekNumber: PropTypes.number,
  }).isRequired,
  currentUserId: PropTypes.number.isRequired,
  weekNumber: PropTypes.number,
  hasUserReachedMaxLocks: PropTypes.bool,
  userLocksCount: PropTypes.number,
  maxPicks: PropTypes.number,
};

export default LeagueLockItem;
